import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Carousel, CarouselContent, CarouselItem } from './ui/carousel';
import { BarChart3, ShoppingCart, Wallet } from 'lucide-react';

const SLIDES = [
  {
    key: 'impulse',
    icon: ShoppingCart,
    tone: 'from-orange-500 to-amber-400',
  },
  {
    key: 'history',
    icon: BarChart3,
    tone: 'from-cyan-500 to-blue-500',
  },
  {
    key: 'budget',
    icon: Wallet,
    tone: 'from-emerald-500 to-lime-500',
  },
];

function SmartOnboardingModal({ open, onClose, t }) {
  const [carouselApi, setCarouselApi] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    if (!carouselApi) {
      return;
    }

    const syncSlide = () => {
      setCurrentSlide(carouselApi.selectedScrollSnap());
    };

    syncSlide();
    carouselApi.on('select', syncSlide);

    return () => {
      carouselApi.off('select', syncSlide);
    };
  }, [carouselApi]);

  if (!open) {
    return null;
  }

  const isLastSlide = currentSlide === SLIDES.length - 1;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="w-full max-w-4xl rounded-[2rem] bg-white shadow-2xl" onClick={(event) => event.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gray-500">SmartInventory</p>
            <h2 className="text-xl font-bold text-gray-900">{t('onboarding.title')}</h2>
          </div>
          <Button variant="outline" onClick={onClose}>{t('onboarding.skip')}</Button>
        </div>

        <div className="p-6">
          <Carousel setApi={setCarouselApi} opts={{ loop: false, align: 'start' }}>
            <CarouselContent>
              {SLIDES.map((slide) => {
                const Icon = slide.icon;
                return (
                  <CarouselItem key={slide.key}>
                    <div className="grid gap-6 lg:grid-cols-[0.8fr_1.2fr] lg:items-center">
                      <div className={`flex min-h-[260px] items-center justify-center rounded-[2rem] bg-gradient-to-br ${slide.tone} p-8 text-white shadow-xl`}>
                        <Icon className="h-28 w-28" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold uppercase tracking-[0.18em] text-gray-500">
                          {t('onboarding.step', { number: currentSlide + 1 })}
                        </p>
                        <h3 className="mt-3 text-3xl font-bold text-gray-900">{t(`onboarding.${slide.key}.headline`)}</h3>
                        <p className="mt-4 text-lg leading-8 text-gray-700">{t(`onboarding.${slide.key}.body`)}</p>
                      </div>
                    </div>
                  </CarouselItem>
                );
              })}
            </CarouselContent>
          </Carousel>

          <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2">
              {SLIDES.map((slide, index) => (
                <button
                  key={`dot-${slide.key}`}
                  type="button"
                  onClick={() => carouselApi?.scrollTo(index)}
                  className={`h-2.5 rounded-full transition ${currentSlide === index ? 'w-10 bg-gray-900' : 'w-2.5 bg-gray-300'}`}
                  aria-label={`Aller au slide ${index + 1}`}
                />
              ))}
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => carouselApi?.scrollPrev()} disabled={currentSlide === 0}>
                {t('onboarding.previous')}
              </Button>
              <Button onClick={() => (isLastSlide ? onClose() : carouselApi?.scrollNext())} className="bg-gray-900 text-white hover:bg-gray-800">
                {isLastSlide ? t('onboarding.finish') : t('onboarding.next')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SmartOnboardingModal;